/* ============================================================
   Dathent — returnPath.js
   Remembers where a signed-out visit to a protected /app route was headed,
   so a login round trip that loses React Router state (a full-page reload
   on /login, or the Google OIDC hop: /login -> auth-worker -> back to
   /login?...) can still land the user back where they meant to go instead
   of the default dashboard/tab. sessionStorage only (per-tab, never a
   cross-session redirect) and cleared as soon as it's read, so a stale
   value can't resurface on a later, unrelated login.
   ============================================================ */

const STORAGE_KEY = 'dathent.returnPath';

// Top-level auth routes — never a valid "return to" target even if somehow
// paired with a /app-looking pathname.
const AUTH_ROUTE_RE = /^\/(login|signup|forgot|reset|confirm)(\/|$)/;

// Only an in-app path is ever memorized/returned: must start with /app (or
// /app/) exactly — not merely as a string prefix (rejects "/appendix") — and
// must not be an auth route. Anything absolute or protocol-relative
// ("https://evil.example/app", "//evil.example/app") fails the /app-prefix
// check on its own since it doesn't start with a single "/app" segment, so
// no separate origin allowlist/URL parsing is needed here.
export function safeReturnPath(pathname, search = '') {
  if (typeof pathname !== 'string' || !pathname) return null;
  if (!/^\/app(\/|$)/.test(pathname)) return null;
  if (AUTH_ROUTE_RE.test(pathname)) return null;
  return `${pathname}${typeof search === 'string' ? search : ''}`;
}

export function rememberReturnPath(path, storage = globalThis?.sessionStorage) {
  if (typeof path !== 'string' || !path) return;
  try {
    storage?.setItem(STORAGE_KEY, path);
  } catch {
    // Storage can be unavailable (private browsing, disabled site data) —
    // the caller's own router state / ?next= remains the primary mechanism.
  }
}

export function takeReturnPath(storage = globalThis?.sessionStorage) {
  try {
    const value = storage?.getItem(STORAGE_KEY);
    storage?.removeItem(STORAGE_KEY);
    return typeof value === 'string' && value ? value : null;
  } catch {
    return null;
  }
}
